import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BuiltInFilterFn,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { calculateColumnWidths, getLeftRightValues } from './blocs/table';
import { DEFAULT_TABLE_KEYBOARD_SCOPE } from './blocs/table-keyboard-scopes';
import {
  Dimensions,
  Platform,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { measureVisibleElement } from '@experiences/components/virtualization/measure-visible-element.web';
import { TABLE_HEADER_BORDER_WIDTH } from './table-header-row';
import { TableBody } from './table-body';
import { TableHead } from './table-head';
import { TableProps } from './types';
import { TableProvider } from './blocs/table-context';
import { useTableKeyboardNavigation } from './blocs/hooks/use-table-keyboard-navigation';
import { useVirtualizer } from '@tanstack/react-virtual';

const DEFAULT_TABLE_FILTER_FN: BuiltInFilterFn = 'includesString';
// Keep a small buffer of rows rendered to avoid scroll jank during arrow nav.
const VIRTUALIZER_OVERSCAN_ROWS = 7;
// Buffer a couple of rows to avoid re-centering when a row is just inside view.
const ROW_VISIBILITY_BUFFER_ROWS = 2;
// Default estimated header height for scroll position calculations.
const DEFAULT_ESTIMATED_HEADER_HEIGHT = 60;
const END_REACHED_THRESHOLD = 0.5;

export const Table = <TData, TValue>({
  cellPadding = 0,
  columns,
  bodyContainerStyle,
  data,
  editableColumns,
  emptyComponent,
  fillAvailableWidth = false,
  filterFromLeafRows,
  getSubRows,
  isActive,
  estimatedHeaderHeight = DEFAULT_ESTIMATED_HEADER_HEIGHT,
  estimatedSectionHeaderHeight = 0,
  estimatedRowHeight,
  globalFilterFn = DEFAULT_TABLE_FILTER_FN,
  gutters,
  headerPadding,
  initialScrollIndex,
  initialState,
  keyboardShortcutScope = DEFAULT_TABLE_KEYBOARD_SCOPE,
  layoutWidth,
  listFooter,
  margin,
  meta,
  onEndReached,
  onFilteredRowsChange,
  onFocusedRowChange,
  onExpandedChange,
  onSortingChange,
  isStickyRow,
  renderHeaderRow,
  renderRow,
  rowVisibilityBottomBufferMultiplier = 2,
  scrollContainerStyle,
  scrollHandleRef,
  sectionHeader,
  state,
}: TableProps<TData, TValue>) => {
  const scrollContainerRef = useRef<HTMLElement>(null);
  const emptyScrollContainerRef = useRef<number | null>(0);
  const prevGlobalFilterRef = useRef<unknown>(undefined);
  // Stable ref to rows so the rangeExtractor closure doesn't go stale between renders.
  // Initialized empty; assigned after rows is computed below.
  const rowsRef = useRef<ReturnType<typeof table.getRowModel>['rows']>([]);
  const [isPinnedShadowVisible, setIsPinnedShadowVisible] = useState(false);

  // Extract values from gutters and margin (support both number and [left, right])
  const [guttersLeft, guttersRight] = getLeftRightValues(gutters ?? 0);
  const [marginLeft, marginRight] = getLeftRightValues(margin ?? 0);

  // Automatically enable global filter if globalFilter or onGlobalFilterChange is provided
  const shouldEnableGlobalFilter = !!state?.globalFilter;

  const columnsWithConfigs = useMemo(() => {
    const columnVisibility = state?.columnVisibility;
    const visibleColumns = fillAvailableWidth
      ? columns.filter(
          (col) =>
            !columnVisibility || !col.id || columnVisibility[col.id] !== false,
        )
      : columns;
    return visibleColumns.map((col) => ({
      id: col.id,
      initialPercentWidth: col.meta?.initialPercentWidth,
      maxSize: col.maxSize,
      minSize: col.minSize,
      size: col.size,
    }));
  }, [columns, fillAvailableWidth, state?.columnVisibility]);

  // Calculate initial column widths using effective layout width
  const initialColumnSizing = useMemo(() => {
    const totalWidth = layoutWidth ?? Dimensions.get('window').width;
    const availableWidth =
      totalWidth - guttersLeft - guttersRight - marginLeft - marginRight;

    return calculateColumnWidths(
      availableWidth,
      columnsWithConfigs,
      fillAvailableWidth,
    );
    // Initial widths only; layout updates are handled in the resize effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  const table = useReactTable({
    columnResizeMode: 'onChange',
    columns,
    data,
    enableColumnResizing: true,
    enableGlobalFilter: shouldEnableGlobalFilter,
    filterFromLeafRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: shouldEnableGlobalFilter
      ? getFilteredRowModel()
      : undefined,
    getRowCanExpand: () => true,
    getSortedRowModel: getSortedRowModel(),
    getSubRows,
    globalFilterFn,
    initialState: {
      columnSizing: initialColumnSizing,
      ...initialState,
    },
    meta,
    onExpandedChange,
    onSortingChange,
    state,
  });

  const { rows } = table.getRowModel();
  // Keep rowsRef current so the rangeExtractor closure never reads stale data
  rowsRef.current = rows;

  useEffect(() => {
    onFilteredRowsChange?.(rows);
  }, [rows, onFilteredRowsChange]);

  // Account for optional section header in table body
  const totalHeaderHeight =
    estimatedHeaderHeight + (estimatedSectionHeaderHeight ?? 0);

  // When isStickyRow is provided, extend the virtual range to always include
  // the sticky parent row for any currently-visible child rows. Without this,
  // a parent row scrolls out of the overscan window and is removed from the DOM
  // while its children are still visible, breaking position:sticky.
  const rangeExtractor = useCallback(
    (range: {
      count: number;
      endIndex: number;
      overscan: number;
      startIndex: number;
    }) => {
      const start = Math.max(0, range.startIndex - range.overscan);
      const end = Math.min(range.count - 1, range.endIndex + range.overscan);
      const indices = new Set<number>();
      for (let i = start; i <= end; i++) {
        indices.add(i);
      }
      // For each visible row, walk upward to find and include its sticky parent
      for (let i = range.startIndex; i <= range.endIndex; i++) {
        const row = rowsRef.current[i];
        if (!row || isStickyRow!(row)) {
          continue;
        }
        for (let j = i - 1; j >= 0; j--) {
          const candidate = rowsRef.current[j];
          if (candidate && isStickyRow!(candidate)) {
            indices.add(j);
            break;
          }
        }
      }
      return [...indices].sort((a, b) => a - b);
    },
    [isStickyRow],
  );

  const initialScrollOffsetRef = useRef(
    typeof initialScrollIndex === 'number'
      ? initialScrollIndex * estimatedRowHeight
      : undefined,
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => estimatedRowHeight,
    getScrollElement: () => scrollContainerRef.current,
    initialOffset: initialScrollOffsetRef.current,
    measureElement: measureVisibleElement,
    overscan: VIRTUALIZER_OVERSCAN_ROWS,
    rangeExtractor: isStickyRow ? rangeExtractor : undefined,
  });
  const items = virtualizer.getVirtualItems();

  /**
   * Returns the top offset and height of a row for visibility and scroll calculations.
   * Used by isRowVisible and scrollToIndex for consistent row geometry.
   * For off-screen rows (not in getVirtualItems()), uses virtualizer.getOffsetForIndex(index)
   * which returns a tuple [offset, align]; we unwrap the offset so scroll-to-focus works
   * when the previously focused row has been scrolled out of view.
   */
  const getRowBounds = useCallback(
    (index: number): { rowHeight: number; rowTop: number } | null => {
      const virtualItem = virtualizer
        .getVirtualItems()
        .find((item) => item.index === index);
      let virtualizerOffset: number | undefined;
      if (virtualItem) {
        virtualizerOffset = virtualItem.start;
      } else {
        const raw = virtualizer.getOffsetForIndex(index);
        virtualizerOffset = Array.isArray(raw) ? raw[0] : undefined;
      }
      if (typeof virtualizerOffset !== 'number') {
        return null;
      }
      const rowTop = totalHeaderHeight + virtualizerOffset;
      return { rowHeight: estimatedRowHeight, rowTop };
    },
    [estimatedRowHeight, totalHeaderHeight, virtualizer],
  );

  /**
   * Height of the scroll container available for rows (viewport minus bottom buffer).
   * Used by isRowVisible and scrollToIndex for consistent viewport calculations.
   */
  const getEffectiveViewportHeight = useCallback(
    (clientHeight: number): number => {
      const rowVisibilityBufferBottom =
        estimatedRowHeight * rowVisibilityBottomBufferMultiplier;
      return clientHeight - rowVisibilityBufferBottom;
    },
    [estimatedRowHeight, rowVisibilityBottomBufferMultiplier],
  );

  // Helper function to check if a row is fully visible using the actual scroll viewport
  // Takes into account the row visibility buffer, excludes virtualizer overscan
  const isRowVisible = useCallback(
    (index: number): boolean => {
      const scrollElement = scrollContainerRef.current;
      if (!scrollElement) {
        return false;
      }
      const bounds = getRowBounds(index);
      if (bounds === null) {
        return false;
      }
      const { rowHeight, rowTop } = bounds;
      const rowBottom = rowTop + rowHeight;
      const rowVisibilityBuffer =
        estimatedRowHeight * ROW_VISIBILITY_BUFFER_ROWS;
      const viewportTop = scrollElement.scrollTop + rowVisibilityBuffer;
      // Bottom of visible region = top of bottom bar in content coords (scrollTop + effectiveViewportHeight).
      const viewportBottom =
        scrollElement.scrollTop +
        getEffectiveViewportHeight(scrollElement.clientHeight);

      const aboveTop = rowBottom > viewportTop;
      const belowBottom = rowTop < viewportBottom;
      const notPastBottom = rowBottom <= viewportBottom;
      const visible = aboveTop && belowBottom && notPastBottom;

      // Row must overlap viewport and not extend below the visible bottom (top of bottom bar)
      return visible;
    },
    [estimatedRowHeight, getEffectiveViewportHeight, getRowBounds],
  );

  /**
   * Scrolls a row into view by index with smooth animation.
   * - For most rows: centers them in the viewport
   * - For the last 2 rows: scrolls to ensure they're fully visible above the bottom bar
   * - Only scrolls if the row is not already visible (accounts for header and bottom bar)
   * - Uses requestAnimationFrame before scrollTo(behavior: 'smooth'), ensuring the virtualizer
   *   has finished updating DOM positions before scrolling
   * - Accounts for header height and TableBody transform when calculating row positions
   *
   * @param index - The index of the row to scroll into view
   */
  const scrollToIndex = useCallback(
    (index: number) => {
      if (index < 0 || index > rows.length) {
        return;
      }

      const scrollElement = scrollContainerRef.current;
      if (!scrollElement) {
        return;
      }

      // If the row is already fully visible (within viewport and above the bottom bar), skip scrolling
      if (isRowVisible(index)) {
        return;
      }

      const effectiveViewportHeight = getEffectiveViewportHeight(
        scrollElement.clientHeight,
      );

      const bounds = getRowBounds(index);
      if (bounds === null) {
        return;
      }
      const { rowHeight, rowTop } = bounds;

      // For the last 2 rows, scroll to show them above the bottom bar
      if (index >= rows.length - 2) {
        // Position row so its bottom is at the effective viewport bottom (above the bar)
        // Formula: rowBottom = scrollTop + effectiveViewportHeight
        // Therefore: scrollTop = rowBottom - effectiveViewportHeight = rowTop + rowHeight - effectiveViewportHeight
        const targetOffset = rowTop + rowHeight - effectiveViewportHeight;

        // Smooth scroll to target - use RAF to ensure virtualizer has finished updating
        const targetScrollTop = Math.max(0, targetOffset);
        requestAnimationFrame(() => {
          scrollElement.scrollTo({ behavior: 'smooth', top: targetScrollTop });
        });
        return;
      }

      // For other rows, center them in the effective viewport
      const centerOffset =
        rowTop +
        rowHeight / 2 -
        effectiveViewportHeight / 2 -
        totalHeaderHeight;

      // Smooth scroll to target - use RAF to ensure virtualizer has finished updating
      const targetScrollTop = Math.max(0, centerOffset);
      requestAnimationFrame(() => {
        scrollElement.scrollTo({ behavior: 'smooth', top: targetScrollTop });
      });
    },
    [
      getEffectiveViewportHeight,
      getRowBounds,
      isRowVisible,
      rows.length,
      totalHeaderHeight,
    ],
  );

  const isEmpty = rows.length === 0;

  const handleFocusedIndexChange = useCallback(
    (index: number | null) => {
      onFocusedRowChange?.(
        index !== null
          ? (table.getRowModel().rows[index]?.original ?? null)
          : null,
      );
    },
    [onFocusedRowChange, table],
  );

  // Keyboard navigation (arrow keys, focus management)
  const { clearFocus, focusedColumn, focusedIndex, tableId } =
    useTableKeyboardNavigation({
      editableColumns,
      isActive,
      keyboardShortcutScope,
      onFocusedIndexChange: handleFocusedIndexChange,
      rowCount: rows.length,
      sorting: state?.sorting,
    });

  // Auto-scroll to focused index when it changes
  useEffect(() => {
    if (
      focusedIndex !== null &&
      focusedIndex >= 0 &&
      focusedIndex < rows.length
    ) {
      scrollToIndex(focusedIndex);
    }
  }, [focusedIndex, scrollToIndex, rows.length, tableId]);

  // Scroll to the top of the table when sorting changes
  useEffect(
    () => virtualizer.scrollToOffset(0, { align: 'start' }),
    [state?.sorting, virtualizer],
  );

  // Scroll to top when local filter (globalFilter) changes.
  // Skip initial mount. When globalFilter is falsy we don't scroll so the restore-scroll effect can run.
  useEffect(() => {
    const filter = state?.globalFilter;
    const filterChanged =
      prevGlobalFilterRef.current !== undefined &&
      prevGlobalFilterRef.current !== filter &&
      filter;

    prevGlobalFilterRef.current = filter;

    if (!filterChanged) {
      return;
    }
    virtualizer.scrollToOffset(0, { align: 'start' });
    clearFocus();
  }, [state?.globalFilter, clearFocus, virtualizer]);

  // Expose imperative scroll control to parent via ref.
  // Assigned in render so the handle always uses the latest virtualizer/tableId.
  if (scrollHandleRef) {
    scrollHandleRef.current = {
      getTopmostVisibleIndex: () => {
        const scrollElement = scrollContainerRef.current;

        if (!scrollElement) {
          return null;
        }

        const virtualItems = virtualizer.getVirtualItems();
        if (virtualItems.length === 0) {
          return null;
        }

        const viewportTop = scrollElement.scrollTop + totalHeaderHeight;
        const topmostItem = virtualItems.find((item) => {
          const itemBottom = totalHeaderHeight + item.start + item.size;
          return itemBottom > viewportTop;
        });

        return topmostItem?.index ?? virtualItems.at(-1)!.index;
      },
      scrollToIndex: (index) => {
        if (index < 0 || index >= rows.length) {
          return;
        }
        const scrollEl = scrollContainerRef.current;
        if (!scrollEl) {
          return;
        }
        virtualizer.scrollToIndex(index, { align: 'start', behavior: 'auto' });
      },
      scrollToTop: () => {
        virtualizer.scrollToOffset(0, { align: 'start' });
        clearFocus();
      },
    };
  }

  /**
   * Restores the scroll position when the global filter is cleared.
   * When a user clears their search, we scroll back to the position they were at
   * before they started searching. This preserves their place in the table and
   * provides a better user experience.
   *
   * Uses virtualizer.scrollToOffset which handles the scroll synchronously before
   * the browser paints, preventing any visible jump or flicker.
   */
  useEffect(() => {
    if (!state?.globalFilter) {
      virtualizer.scrollToOffset(emptyScrollContainerRef.current ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.globalFilter]);

  /**
   * Saves the current scroll position when there's no active search filter.
   * This continuously tracks the user's scroll position while they're browsing
   * the unfiltered table, so we can restore it later when they clear a search.
   *
   * Only saves the position when globalFilter is empty to avoid overwriting
   * the saved position while the user is actively searching.
   */
  useEffect(() => {
    if (!state?.globalFilter) {
      emptyScrollContainerRef.current = virtualizer.scrollOffset;
    }
  }, [state?.globalFilter, virtualizer.scrollOffset]);

  // Memoize the column widths rather than referencing them per-cell render
  // to optimize performance for large data sets (as recommended by TanStack)
  const columnWidths = useMemo(
    () => table.getState().columnSizing,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table.getState().columnSizing],
  );

  // Auto-scroll horizontally to keep the focused column visible.
  // RAF-debounced so intermediate column changes (disabled cell skips) are ignored —
  // only the final settled column triggers a scroll.
  const pendingColumnScrollRef = useRef<number | null>(null);
  useEffect(() => {
    if (pendingColumnScrollRef.current) {
      cancelAnimationFrame(pendingColumnScrollRef.current);
      pendingColumnScrollRef.current = null;
    }

    if (!focusedColumn) {
      return;
    }

    pendingColumnScrollRef.current = requestAnimationFrame(() => {
      pendingColumnScrollRef.current = null;

      const scrollElement = scrollContainerRef.current;
      if (!scrollElement) {
        return;
      }

      const allColumns = table.getAllLeafColumns();
      const pinnedIds = new Set(table.getLeftLeafHeaders().map((h) => h.id));
      const pinnedWidth = table
        .getLeftLeafHeaders()
        .reduce((sum, h) => sum + (columnWidths[h.id] ?? 0), 0);

      // Skip scroll for pinned columns (always visible)
      if (pinnedIds.has(focusedColumn)) {
        return;
      }

      let columnLeft = 0;
      for (const col of allColumns) {
        if (col.id === focusedColumn) {
          break;
        }
        columnLeft += columnWidths[col.id] ?? 0;
      }
      const columnRight = columnLeft + (columnWidths[focusedColumn] ?? 0);

      const viewLeft = scrollElement.scrollLeft + pinnedWidth;
      const viewRight = scrollElement.scrollLeft + scrollElement.clientWidth;

      if (columnLeft < viewLeft) {
        scrollElement.scrollTo({
          behavior: 'smooth',
          left: columnLeft - pinnedWidth,
        });
      } else if (columnRight > viewRight) {
        scrollElement.scrollTo({
          behavior: 'smooth',
          left: columnRight - scrollElement.clientWidth,
        });
      }
    });

    return () => {
      if (pendingColumnScrollRef.current) {
        cancelAnimationFrame(pendingColumnScrollRef.current);
        pendingColumnScrollRef.current = null;
      }
    };
  }, [focusedColumn, columnWidths, table]);

  // Calculate the minimum width based on sum of visible column minSizes
  const tableMinWidth = useMemo(() => {
    const columnVisibility = state?.columnVisibility;
    return columns.reduce((sum, col) => {
      if (columnVisibility && col.id && !columnVisibility[col.id]) {
        return sum;
      }
      return sum + (col.minSize ?? 0);
    }, 0);
  }, [columns, state?.columnVisibility]);

  // Updates column widths in response to changes in layout or window width
  useLayoutEffect(() => {
    const node = scrollContainerRef.current;
    const scrollbarWidth = (node?.offsetWidth ?? 0) - (node?.clientWidth ?? 0);
    const totalWidth = layoutWidth ?? Dimensions.get('window').width;
    const availableWidth =
      totalWidth -
      guttersLeft -
      guttersRight -
      marginLeft -
      marginRight -
      scrollbarWidth;

    const newColumnWidths = calculateColumnWidths(
      availableWidth,
      columnsWithConfigs,
      fillAvailableWidth,
    );
    table.setColumnSizing(newColumnWidths);
  }, [
    columns,
    columnsWithConfigs,
    fillAvailableWidth,
    guttersLeft,
    guttersRight,
    layoutWidth,
    marginLeft,
    marginRight,
    table,
  ]);

  // Total width of all left-pinned columns, used to position the shadow overlay
  const pinnedColumnWidth = useMemo(() => {
    return table
      .getLeftLeafHeaders()
      .reduce((sum, header) => sum + (columnWidths[header.id] ?? 0), 0);
  }, [table, columnWidths]);

  // Show the pinned column shadow when the table has been scrolled horizontally
  useEffect(() => {
    const node = scrollContainerRef.current;
    if (node) {
      const handleScroll = () => setIsPinnedShadowVisible(node.scrollLeft > 0);
      node.addEventListener('scroll', handleScroll, { passive: true });
      return () => node.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    const node = scrollContainerRef.current;
    if (!node || !onEndReached) {
      return;
    }
    let prevNearEnd = false;
    const checkEndReached = () => {
      const distanceFromEnd =
        node.scrollHeight - node.scrollTop - node.clientHeight;
      const currentNearEnd =
        distanceFromEnd <= node.clientHeight * END_REACHED_THRESHOLD;
      if (currentNearEnd && !prevNearEnd) {
        onEndReached();
      }
      prevNearEnd = currentNearEnd;
    };
    node.addEventListener('scroll', checkEndReached, { passive: true });
    checkEndReached();
    return () => node.removeEventListener('scroll', checkEndReached);
  }, [onEndReached]);

  const renderTableBody = () => {
    if (isEmpty) {
      return null;
    }

    return (
      <TableBody
        style={StyleSheet.flatten([
          isStickyRow
            ? // Position the TableBody absolutely at the correct scroll offset so
              // that position:sticky children (group headers) use the right layout
              // position for their sticky threshold calculations.
              // transform: translateY() would move rows visually but not in layout,
              // causing sticky rows to always activate (layout pos ≈ header height < sticky top).
              // The header border counts: start the body below it so the first
              // sticky row's layout position matches its sticky threshold —
              // otherwise it is pinned (and shifted down) at rest, hiding its
              // own bottom divider under the next row.
              {
                left: 0,
                position: 'absolute' as ViewStyle['position'],
                right: 0,
                top:
                  totalHeaderHeight +
                  TABLE_HEADER_BORDER_WIDTH +
                  (items[0]?.start ?? 0),
              }
            : // Offset the TableBody by the first visible item's position to align
              // the virtualized rows correctly within the scrollable container
              { transform: `translateY(${items[0]?.start ?? 0}px)` },
          bodyContainerStyle,
        ])}
      >
        {items.map((virtualRow, i) => {
          const row = rows[virtualRow.index];
          // When a sticky parent row is kept in the range by rangeExtractor
          // while its children have scrolled past the overscan boundary, there
          // is a positional gap between it and the next rendered item. Insert a
          // spacer to fill that gap so subsequent rows stay visually aligned.
          const prevItem = i > 0 ? items[i - 1] : undefined;
          const gapHeight = prevItem
            ? virtualRow.start - (prevItem.start + prevItem.size)
            : 0;
          return (
            <React.Fragment key={virtualRow.index}>
              {gapHeight > 0 && <View style={{ height: gapHeight }} />}
              {renderRow({ index: virtualRow.index, row })}
            </React.Fragment>
          );
        })}
      </TableBody>
    );
  };

  return (
    <TableProvider
      value={{
        cellPadding,
        columnWidths,
        headerPadding,
        isActive,
        onFocusedIndexChange: handleFocusedIndexChange,
        scrollToIndex,
        tableId,
        virtualizer,
      }}
    >
      <View
        ref={scrollContainerRef as unknown as React.RefObject<View>}
        style={[
          styles.scrollContainer,
          { paddingLeft: guttersLeft, paddingRight: guttersRight },
          scrollContainerStyle,
        ]}
      >
        <View
          role="table"
          style={[
            { minWidth: tableMinWidth },
            { marginLeft: marginLeft, marginRight: marginRight },
            !isEmpty && {
              height: virtualizer.getTotalSize() + totalHeaderHeight,
            },
          ]}
        >
          <TableHead>
            {table
              .getHeaderGroups()
              .map((headerGroup) => renderHeaderRow({ headerGroup }))}
          </TableHead>
          {sectionHeader ? (
            <View style={styles.sectionHeaderRow}>
              <View style={styles.sectionHeaderPinned}>{sectionHeader}</View>
            </View>
          ) : null}

          {renderTableBody()}
        </View>
        {isEmpty && isActive ? (
          <View style={styles.emptyStateContainer}>{emptyComponent}</View>
        ) : null}
        {/* box-none: let events pass through the wrapper to the table while
            keeping footer children (e.g. CTAs) interactive. */}
        {listFooter && !isEmpty ? (
          <View
            pointerEvents="box-none"
            style={[
              styles.listFooterContainer,
              {
                width:
                  (layoutWidth ?? Dimensions.get('window').width) -
                  guttersLeft -
                  guttersRight,
              },
              bodyContainerStyle,
            ]}
          >
            {listFooter}
          </View>
        ) : null}
      </View>
      {isPinnedShadowVisible && pinnedColumnWidth > 0 ? (
        <LinearGradient
          colors={['rgba(0, 0, 0, 0.05)', 'rgba(0, 0, 0, 0)']}
          end={[1, 0]}
          start={[0, 0]}
          style={[
            styles.pinnedColumnShadow,
            { left: guttersLeft + pinnedColumnWidth },
          ]}
        />
      ) : null}
    </TableProvider>
  );
};

const styles = StyleSheet.create({
  emptyStateContainer: {
    ...Platform.select({
      web: { position: 'sticky' as ViewStyle['position'] },
    }),
    alignItems: 'center',
    alignSelf: 'stretch',
    flex: 1,
    justifyContent: 'center',
    left: 0,
    pointerEvents: 'box-none',
  },
  listFooterContainer: {
    ...Platform.select({
      web: { position: 'sticky' as ViewStyle['position'] },
    }),
    left: 0,
  },
  pinnedColumnShadow: {
    bottom: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    width: 8,
  },
  scrollContainer: {
    // Prevent table from stretching beyond its content
    alignItems: 'flex-start',
    flex: 1,
    overflow: 'auto' as ViewStyle['overflow'],
  },
  sectionHeaderPinned: {
    ...Platform.select({
      web: { position: 'sticky' as ViewStyle['position'] },
    }),
    left: 0,
    zIndex: 1,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    overflow: 'visible' as ViewStyle['overflow'],
  },
});
